import React from 'react';
import { Link } from 'react-router-dom';
import './Primitives.css';

export function PageHeader({ eyebrow, title, description, actions, children, className = '' }) {
  return (
    <header className={`say-page-header ${className}`.trim()}>
      <div className="say-page-header-copy">
        {eyebrow ? <span className="say-eyebrow">{eyebrow}</span> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
        {children}
      </div>
      {actions ? <div className="say-page-header-actions">{actions}</div> : null}
    </header>
  );
}

export function SectionHeading({ title, description, action, eyebrow, className = '' }) {
  return (
    <div className={`say-section-heading ${className}`.trim()}>
      <div>
        {eyebrow ? <span className="say-eyebrow">{eyebrow}</span> : null}
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action ? <div className="say-section-action">{action}</div> : null}
    </div>
  );
}

export function Surface({ as: Component = 'section', children, className = '', padding = 'default' }) {
  return <Component className={`say-surface say-surface-${padding} ${className}`.trim()}>{children}</Component>;
}

export function Button({
  as,
  to,
  href,
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  type = 'button',
  loading = false,
  loadingLabel,
  disabled = false,
  ...props
}) {
  const classes = `say-button-new say-button-${variant} say-button-${size} ${className}`.trim();
  const content = <BusyContent busy={loading} label={loadingLabel}>{children}</BusyContent>;
  if (to) return <Link to={to} className={classes} {...props}>{content}</Link>;
  if (href) return <a href={href} className={classes} {...props}>{content}</a>;
  const Component = as || 'button';
  return <Component type={Component === 'button' ? type : undefined} className={classes} disabled={disabled || loading} aria-busy={loading || undefined} {...props}>{content}</Component>;
}

export function StatusBadge({ status = 'neutral', children, dot = true, className = '' }) {
  return (
    <span className={`say-status say-status-${status} ${className}`.trim()}>
      {dot ? <span className="say-status-dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}

export function Alert({ tone = 'info', title, children, actions, className = '' }) {
  return (
    <div className={`say-alert say-alert-${tone} ${className}`.trim()} role={tone === 'danger' ? 'alert' : 'status'}>
      <div className="say-alert-copy">
        {title ? <strong>{title}</strong> : null}
        {children ? <div>{children}</div> : null}
      </div>
      {actions ? <div className="say-alert-actions">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({ icon, title, description, action, className = '' }) {
  return (
    <div className={`say-empty-state ${className}`.trim()}>
      {icon ? <div className="say-empty-icon" aria-hidden="true">{icon}</div> : null}
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action ? <div className="say-empty-action">{action}</div> : null}
    </div>
  );
}

export function Skeleton({ width = '100%', height = 16, className = '' }) {
  return <span className={`say-skeleton ${className}`.trim()} style={{ width, height }} aria-hidden="true" />;
}

// Keep the idle content measured while the spinner replaces it visually.
export function BusyContent({ busy, label, children }) {
  return <span className="say-busy-content" aria-busy={busy || undefined}>
    <span className={busy ? 'say-busy-placeholder' : undefined} aria-hidden={busy || undefined}>{children}</span>
    {busy ? <span className="say-busy-overlay"><span className="say-button-spinner" aria-hidden="true" /><span className="say-loading-description">{label || children}</span></span> : null}
  </span>;
}

export function LoadingIndicator({ label = 'Loading…', className = '' }) {
  return <span className={`say-loading-indicator ${className}`.trim()} role="status" aria-busy="true">
    <span className="say-button-spinner" aria-hidden="true" />
    <span className="say-loading-description">{label}</span>
  </span>;
}

export function LoadingState({ label = 'Loading…', page = false }) {
  return <div className={`say-loading-state${page ? ' say-loading-page' : ''}`} role="status" aria-busy="true">
    <span className="say-loading-description">{label}</span>
    {page ? <span className="say-button-spinner" aria-hidden="true" /> : <div className="say-loading-skeletons"><Skeleton width="45%" height={22} /><Skeleton height={48} /><Skeleton width="75%" height={18} /></div>}
  </div>;
}
